import { Box, Button, Card, CardBody, CardFooter, CardHeader, Paragraph, Spinner, Text, Tip } from "grommet"
import { Chat, Close, Money } from "grommet-icons"
import { Twitch } from "../../icons/Twitch"
import { useDispatch } from "react-redux"
import { removeDonateById } from "../../slices/donatesSlice"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { addLot, updateLotCostByIndex } from "../../slices/auctionDataSlice"
import { forwardRef, useLayoutEffect, useRef, useState } from "react"
import { formattedPrice, formattedPriceNumber } from "../../utils/priceUtils"
import { LinkRenderer } from "../layout/LinkRenderer"
import "./DonateCard.css";
import styled from "styled-components"

export const DonateCard = forwardRef(({ item, ...props }, ref) => {

    const { t, ready } = useTranslation('donate-card', { useSuspense: false });
    const dispatch = useDispatch();
    const ws = useSelector(state => state.ws.clients['donates']);
    const connected = useSelector(state => state.ws.connected['donates']);

    const onRemove = () => {
        dispatch(removeDonateById(item.id));
        if (ws !== undefined && connected !== undefined && connected) {
            ws.publish({ destination: '/app/donates/remove', body: JSON.stringify({ id: item.id }) });
        }
    }
    const onAddNew = () => {
        dispatch(addLot({ lotName: `${item.message}${item.appendUsername ? ` (${item.sender})` : ''}`, cost: formattedPriceNumber(item.currencyAmount) }))
        dispatch(removeDonateById(item.id))
        if (ws !== undefined && connected !== undefined && connected) {
            ws.publish({ destination: '/app/donates/remove', body: JSON.stringify({ id: item.id }) });
        }
    }
    const onAddExisting = () => {
        const [lotIndex] = item.suggestedLot;
        dispatch(updateLotCostByIndex({ index: lotIndex, cost: formattedPriceNumber(item.currencyAmount) }))
        dispatch(removeDonateById(item.id))
        if (ws !== undefined && connected !== undefined && connected) {
            ws.publish({ destination: '/app/donates/remove', body: JSON.stringify({ id: item.id }) });
        }
    }

    if (!ready) {
        return (
            <Spinner/>
        )
    }

    if (ready) {
        return (
            <Card key={item.id} ref={ref} {...props} background="dark-1" margin={{bottom: 'medium'}}>
                <CardHeader pad={{ top: 'medium', left: 'medium', bottom: 'small', right: 'medium' }}>
                    <Box direction="row" gap="small" align="center">
                        {item.donate && (<Money/>)}
                        {!item.donate && (<Twitch/>)}
                        <Text weight="bold" truncate="tip">{formattedPrice(item.currencyAmount)} ₴</Text>
                    </Box>
                    <Button icon={<Close/>} onClick={onRemove}/>
                </CardHeader>
                <CardBody gap="xsmall" pad={{ top: 'none', left: 'medium', bottom: 'medium', right: 'medium' }}>
                    <Box>
                        <Text weight="bold" truncate="tip" size="medium">{item.sender}</Text>
                    </Box>
                    <Box direction="row" gap="xsmall" round="xsmall" background="#4f4f4f" pad="small" style={{ minHeight: '96px' }}>
                        <Chat size="small" style={{ margin: "4px 0 0 0" }} />
                        <MessageBlock message={<LinkRenderer text={item.message} />} />
                    </Box>
                </CardBody>
                <CardFooter justify="center" pad={{ top: 'none', left: 'medium', bottom: 'medium', right: 'medium' }}>
                    {!item.suggestedLot && (
                        <Button onClick={onAddNew} secondary fill label={t('addButton')} size="small"/>
                    )}
                    {item.suggestedLot && (
                        <Box direction="column" fill="horizontal">
                            <Button onClick={onAddNew} secondary label={t('addNewButton')} margin={{ bottom: 'small' }} size="small"/>
                            <Button onClick={onAddExisting} secondary size="small" label={<Box><Text truncate="tip" size="small" style={{ lineHeight: '24px' }}>{t('addToButton') + item.suggestedLot[1]}</Text></Box>} />
                        </Box>
                    )}
                </CardFooter>
            </Card>
        )
    }
})

const MultiLineTruncatedText = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word; /* Ensures long words don't break layout */
`;

const MessageBlock = ({ message }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // 2. Logic to detect overflow
  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const checkTruncation = () => {
      // If the total scrollable height is greater than the visible height,
      // it means text is being hidden/truncated.
      // We use a tiny buffer (+1) to account for sub-pixel rendering differences.
      setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    };

    // Check immediately
    checkTruncation();

    // Check whenever the element resizes (responsive width changes)
    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [message]); // Re-run if the message content itself changes

  // 3. Define the text component to avoid repetition
  // We attach the ref here to measure this specific element
  const TextComponent = (
    <MultiLineTruncatedText ref={textRef} size="medium">
      {message}
    </MultiLineTruncatedText>
  );

  return (
    <Box>
      {/* 4. Conditionally wrap with Tip */}
      {isTruncated ? (
        <Tip 
          content={
            <Box pad="small" width={{ max: 'medium' }}>
              <Text>{message}</Text>
            </Box>
          }
        >
          {TextComponent}
        </Tip>
      ) : (
        TextComponent
      )}
    </Box>
  );
};